import { useCallback, useEffect, useRef, useState } from "react";
import { ProductItemInterface } from "@/assets/interfaces";
import CartContext from "@/contexts/cart-context";
import useAnalytics, { PAGE_TYPE } from "@/components/hooks/analytics";
import { useModals } from "@/components/hooks/useModals";
import Portal from "@/components/portal";
import ImageCarouselModal from "@/components/products/image-carousel-modal";
import Modal, { ModalBody } from "@/components/ui/modal-full";
import ProductGallery from "@/components/ui/product-gallery";
import useTabs from "@/components/hooks/useTabs";
import ShareProductModal from "../share-product";
import RelatedProducts from "../../items/related-products";
import { useRequest } from "@/api/utils";
import { GetRelatedItems } from "@/api/items";
import useLockPageScroll from "@/components/hooks/useLockPageScroll";
import ProductInfo from "./product-info";
import ProductOptions from "./product-options";
import QuantitySelector from "./quantity-selector";
import ActionButtons from "./action-buttons";
import InfoBlocksSection from "./info-blocks";
import CartContextV2 from "@/contexts/cart-context-v2";

interface Props {
  show: boolean;
  toggle: (state: boolean) => void;
  item: ProductItemInterface;
  itemPage?: boolean;
  setSelectedItem: (item: ProductItemInterface) => void;
}

const ProductDetailsModal = ({ show, toggle, item, itemPage, setSelectedItem }: Props) => {
  const { modals, toggleModal } = useModals(["image_carousel", "share_product"]);
  const [currentImage, setCurrentImage] = useState(0);
  const {
    addOrRemoveItem,
    isItemInCart,
    cart,
    toggleModal: toggleModalFromCart,
    formatAsCurrency,
    setQuantity,
  } = CartContextV2.useContainer();
  const [related, setRelated] = useState<ProductItemInterface[]>([]);
  const relatedData = useRequest(GetRelatedItems);
  const modalBodyRef = useRef<HTMLDivElement>(null);
  const descBodyRef = useRef<HTMLDivElement>(null);

  useLockPageScroll(show);

  const getRelated = useCallback(async () => {
    const [res, err] = await relatedData.makeRequest({ id: item?.id, limit: 5 });

    if (res) {
      const data = res.data;
      setRelated(data);
    }
    if (err) {
      console.log(err);
    }
  }, [item?.id]);

  useEffect(() => {
    if (modalBodyRef.current) {
      modalBodyRef.current.scrollTo(0, 0);
    }
    if (descBodyRef.current) {
      descBodyRef.current.scrollTo(0, 0);
    }
  }, [item]);

  useEffect(() => {
    getRelated();
  }, [getRelated]);

  const tabs = useTabs(["Product Details", "Customer Reviews"], 0);
  const [qty, setQty] = useState<number>(1);

  const handleItemButtonClick = (item: ProductItemInterface) => {
    var variants = [];
    if (item.variants && item.variants.options && item.variants.options.length > 1) {
      addOrRemoveItem(item);
      return;
    }
    addOrRemoveItem(item, variants, [qty]);
    setQuantity(item.id, qty);
    setQty(1);
  };

  const { saveAnalytics } = useAnalytics(PAGE_TYPE.ITEM);

  const cartItem = cart.find(({ item_id }) => item_id === item.id);

  useEffect(() => {
    if (show) {
      saveAnalytics({ store: item.store, item: item.id, itemName: item.name });
    }
  }, [show]);

  const showFullImage = (index: number) => {
    setCurrentImage(index);
    toggleModal("image_carousel");
  };

  return (
    <>
      <Modal
        {...{ show, toggle }}
        title={item.name}
        closeable={!itemPage}
        noBg={itemPage}
        shareAction={() => toggleModal("share_product")}
      >
        <ModalBody setRef={(el) => (modalBodyRef.current = el)} className="!px-0 flex flex-col lg:gap-6.75 !pb-50">
          <div className="w-full block px-5 sm:px-9 md:px-12.5">
            <div className="store-container space-y-6 sm:space-y-8 md:space-y-15">
              <div
                className={`flex flex-col lg:flex-row gap-8 md:gap-11.5 items-stretch ${
                  item?.images?.length + item?.videos?.length > 1 ? "lg:aspect-[1/0.54]" : "lg:aspect-[2/1]"
                }`}
              >
                <div className="w-full flex-1 min-w-[350px] h-full">
                  <div className="w-full max-w-[500px] lg:max-w-full bg-white flex flex-col justify-center items-center">
                    <ProductGallery images={item?.images} videos={item?.videos} />
                  </div>
                </div>
                <div
                  ref={descBodyRef}
                  className={`w-full h-full flex-1 flex flex-col min-w-[350px] relative overflow-y-auto scrollbar-hide pb-5`}
                >
                  <div className="flex-1">
                    <ProductInfo item={item} formatAsCurrency={formatAsCurrency} />
                    <ProductOptions item={item} onSelectOptions={() => addOrRemoveItem(item)} />
                    <InfoBlocksSection infoBlocks={item?.info_blocks} />
                  </div>

                  <div className="fixed z-[50] lg:z-auto lg:sticky w-full bottom-0 left-0 right-0 flex flex-col gap-2 px-5 md:px-9 lg:px-0 py-5 bg-white border-t border-grey-border border-opacity-50 shadow-top">
                    {isItemInCart(item?.id) && cartItem ? (
                      <ActionButtons
                        isInCart={true}
                        onAddToCart={() => addOrRemoveItem(item)}
                        onOpenCart={() => toggleModalFromCart("cart")}
                        onCloseModal={() => toggle(false)}
                      />
                    ) : (
                      <>
                        {(!item?.variants || !item?.variants?.options || item?.variants?.options.length < 1) && (
                          <QuantitySelector quantity={qty} setQuantity={setQty} maxQuantity={item?.quantity} />
                        )}

                        <ActionButtons
                          isInCart={false}
                          onAddToCart={() => handleItemButtonClick(item)}
                          onOpenCart={() => toggleModalFromCart("cart")}
                          onCloseModal={() => toggle(false)}
                        />
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
          <RelatedProducts
            items={relatedData?.response?.data}
            classNames="pb-30"
            productsLoading={relatedData.isLoading}
            setSelectedItem={setSelectedItem}
          />
        </ModalBody>
      </Modal>

      {item?.images && (
        <Portal>
          <ImageCarouselModal
            show={modals.image_carousel}
            toggle={() => toggleModal("image_carousel")}
            images={item?.images}
            current={currentImage}
          />
        </Portal>
      )}

      <Portal>
        <ShareProductModal
          show={modals.share_product}
          toggle={() => toggleModal("share_product")}
          productSlug={item?.slug}
        />
      </Portal>
    </>
  );
};

export default ProductDetailsModal;
